import { useCallback, useEffect, useRef, useState } from "react";
import {
  getFeedMuted,
  setFeedMutedFromUser,
  subscribeFeedMuted,
} from "../../services/videoFeedCoordinator";
import "./VideoControls.css";

/**
 * Custom chrome for the native <video> element.
 *
 * Vimeo's embed brought its own controls; playing the file directly means we
 * draw them. One component covers the three approved layouts — they are the
 * same controls with different framing, not three implementations:
 *
 *   minimal  feed        auto-hides, leaves a hairline progress line
 *   bar      videos      persistent frosted bar, elapsed + remaining
 *   course   percorso    bar + a tick at the % that completes the pill
 *
 * State is read from the media element rather than mirrored in props, so the
 * controls can never disagree with what is actually playing.
 */

const ICONS = {
  play: <path d="M8 5v14l11-7z" />,
  pause: (
    <>
      <rect x="6" y="5" width="4" height="14" rx="1.2" />
      <rect x="14" y="5" width="4" height="14" rx="1.2" />
    </>
  ),
};

const STROKE = {
  volume: (
    <>
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      <path d="M16.5 8.5a5 5 0 0 1 0 7" />
      <path d="M19 6a8.5 8.5 0 0 1 0 12" />
    </>
  ),
  muted: (
    <>
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      <path d="M17 9.5l4 5" />
      <path d="M21 9.5l-4 5" />
    </>
  ),
  expand: <path d="M5 9V5h4M15 5h4v4M19 15v4h-4M9 19H5v-4" />,
  collapse: <path d="M9 5v4H5M19 9h-4V5M15 19v-4h4M5 15h4v4" />,
  // "Guarda su un altro dispositivo": a screen on a stand.
  tv: (
    <>
      <rect x="3" y="5" width="18" height="12" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
};

const fmt = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

export default function VideoControls({
  videoRef,
  variant = "bar",
  // 0..1 — draws the completion threshold on the bar (course pills).
  gateAt = null,
  isFullscreen = false,
  onToggleFullscreen,
  // "Guarda su un altro dispositivo" (movement videos only; the caller decides).
  onOtherDevice,
  // Feed only: a tap on the video while the controls are faded out.
  onSurfaceTap,
  // Hides the whole bar; the caller still owns the thumbnail overlay.
  hidden = false,
  // False for the course: its videos keep their own sound state instead of
  // reading and writing the app-wide preference.
  sharedMute = true,
  // Told about the member's own play / pause / seek just before it happens, so
  // the playback telemetry never mistakes a pause made here for one nobody
  // asked for.
  onUserAction,
}) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(() =>
    sharedMute ? getFeedMuted() : !!videoRef?.current?.muted,
  );
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [idle, setIdle] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const idleTimerRef = useRef(null);
  const barRef = useRef(null);

  // Every layout hides its controls while the video plays and brings them back
  // on a tap — the difference between the variants is how they look, not how
  // they behave. Paused videos keep their controls up: there is nothing to
  // watch underneath them.
  const HIDE_AFTER_MS = 5000;

  // ── Follow the element, don't mirror it ────────────────────────────────
  useEffect(() => {
    const el = videoRef?.current;
    if (!el) return undefined;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => setTime(el.currentTime || 0);
    const onMeta = () => setDuration(el.duration || 0);
    const onVolume = () => setMuted(!!el.muted);

    onMeta();
    onVolume();
    setPlaying(!el.paused);

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    el.addEventListener("volumechange", onVolume);

    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      el.removeEventListener("volumechange", onVolume);
    };
  }, [videoRef]);

  // Muting one video mutes them all — the preference is shared and persisted,
  // so honour changes made by any other player while this one is on screen.
  // Course players opt out and follow only their own element.
  useEffect(() => {
    if (!sharedMute) return undefined;
    return subscribeFeedMuted((next) => setMuted(!!next));
  }, [sharedMute]);

  const poke = useCallback(() => {
    setIdle(false);
    clearTimeout(idleTimerRef.current);
    // Only a playing video hides its controls; while paused or scrubbing they
    // stay put, so the user isn't chasing a bar that vanishes under them.
    const el = videoRef?.current;
    if (el && !el.paused) {
      idleTimerRef.current = setTimeout(() => setIdle(true), HIDE_AFTER_MS);
    }
  }, [videoRef]);

  useEffect(() => {
    poke();
    return () => clearTimeout(idleTimerRef.current);
  }, [playing, poke]);

  const togglePlay = () => {
    const el = videoRef?.current;
    if (!el) return;
    if (el.paused) {
      onUserAction?.("play");
      // Apply the shared preference before starting. Playing straight from
      // here would bypass the player's own start path, so a video begun from
      // these controls came back with sound even though the user had muted
      // elsewhere. Course players keep whatever this element was set to.
      if (sharedMute) el.muted = getFeedMuted();
      el.play().catch(() => {});
    } else {
      onUserAction?.("pause");
      el.pause();
    }
    poke();
  };

  const toggleMute = () => {
    const el = videoRef?.current;
    if (!el) return;
    const next = !el.muted;
    el.muted = next;
    // Persist through the coordinator so every other player follows — except
    // in the course, where the choice applies to this video only.
    if (sharedMute) setFeedMutedFromUser(next);
    setMuted(next);
    poke();
  };

  const seekFromPointer = (event) => {
    const el = videoRef?.current;
    const bar = barRef.current;
    if (!el || !bar || !Number.isFinite(el.duration)) return;
    const rect = bar.getBoundingClientRect();
    const x = (event.clientX ?? 0) - rect.left;
    const ratio = Math.max(0, Math.min(1, rect.width ? x / rect.width : 0));
    el.currentTime = ratio * el.duration;
    setTime(el.currentTime);
    poke();
  };

  const onBarKeyDown = (event) => {
    const el = videoRef?.current;
    if (!el || !Number.isFinite(el.duration)) return;
    if (event.key === "ArrowRight") {
      el.currentTime = Math.min(el.duration, el.currentTime + 5);
    } else if (event.key === "ArrowLeft") {
      el.currentTime = Math.max(0, el.currentTime - 5);
    } else {
      return;
    }
    event.preventDefault();
    setTime(el.currentTime);
  };

  const progress = duration > 0 ? Math.min(1, time / duration) : 0;
  const remaining = Math.max(0, duration - time);
  const gateReached = gateAt != null && progress >= gateAt;

  if (hidden) return null;

  return (
    <div
      className={
        "vc" +
        (variant === "bar" || variant === "course" ? " vc--bar" : "") +
        (idle ? " vc--idle" : "")
      }
      onPointerMove={poke}
      // While hidden the overlay takes the tap itself and just brings the
      // controls back, so the first tap can never also pause the video.
      onPointerDown={idle ? poke : undefined}
      // Feed: that same tap (hidden controls, so never on a button) opens the
      // video full screen.
      onClick={
        onSurfaceTap
          ? (e) => {
              if (!e.target.closest?.(".vc-row")) onSurfaceTap(e);
            }
          : undefined
      }
    >
      {/* What's left once the controls fade: a hairline of progress, so the
          video never looks like it has lost its position. */}
      <div className="vc-hairline" aria-hidden="true">
        <i style={{ width: `${progress * 100}%` }} />
      </div>

      <div className="vc-scrim" aria-hidden="true" />

      <div className="vc-row">
        <button
          type="button"
          className="vc-btn"
          onClick={togglePlay}
          aria-label={playing ? "Metti in pausa" : "Riproduci"}
        >
          <svg viewBox="0 0 24 24" className="vc-solid">
            {playing ? ICONS.pause : ICONS.play}
          </svg>
        </button>

        <span className="vc-time">
          {fmt(time)}
          {variant !== "minimal" && duration > 0 ? (
            <>
              <span className="vc-sep">/</span>
              {`-${fmt(remaining)}`}
            </>
          ) : null}
        </span>

        <div
          ref={barRef}
          className="vc-seek"
          role="slider"
          tabIndex={0}
          aria-label="Avanzamento video"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          onKeyDown={onBarKeyDown}
          onPointerDown={(e) => {
            onUserAction?.("seek");
            e.currentTarget.setPointerCapture?.(e.pointerId);
            setScrubbing(true);
            seekFromPointer(e);
          }}
          onPointerMove={(e) => scrubbing && seekFromPointer(e)}
          onPointerUp={() => setScrubbing(false)}
          onPointerCancel={() => setScrubbing(false)}
        >
          <div className="vc-track">
            <div className="vc-fill" style={{ width: `${progress * 100}%` }} />
          </div>

          {/* Course pills complete at a threshold; showing it turns "the
              button is greyed out" into "this is how much is left". */}
          {gateAt != null ? (
            <span
              className={"vc-gate" + (gateReached ? " vc-gate--done" : "")}
              style={{ left: `${gateAt * 100}%` }}
              aria-hidden="true"
            />
          ) : null}

          <span
            className="vc-knob"
            style={{ left: `${progress * 100}%` }}
            aria-hidden="true"
          />
        </div>

        <button
          type="button"
          className="vc-btn"
          onClick={toggleMute}
          aria-label={muted ? "Riattiva audio" : "Disattiva audio"}
        >
          <svg viewBox="0 0 24 24">{muted ? STROKE.muted : STROKE.volume}</svg>
        </button>

        {onOtherDevice ? (
          <button
            type="button"
            className="vc-btn"
            onClick={onOtherDevice}
            aria-label="Guarda su un altro dispositivo"
            title="Guarda su un altro dispositivo"
          >
            <svg viewBox="0 0 24 24">{STROKE.tv}</svg>
          </button>
        ) : null}

        {onToggleFullscreen ? (
          <button
            type="button"
            className="vc-btn"
            onClick={onToggleFullscreen}
            aria-label={
              isFullscreen
                ? "Esci da schermo intero"
                : "Apri video a schermo intero"
            }
          >
            <svg viewBox="0 0 24 24">
              {isFullscreen ? STROKE.collapse : STROKE.expand}
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}
